import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../common/breadcrumbs";
import * as userServices from "../../services/users.services";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { globalLoader, handleServerValidations } from "../../../../utils/commonfunction";
import { useTranslation } from "react-i18next";

const UserEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  // const [isResetPassword, setIsResetPassword] = useState(false);

  const [initialValues, setInitialValues] = useState({
    fullName: "",
    address: "",
    profile_image: "",
    country: "",
    city: "",
    state: "",
    zip_code: "",
    // newPassword: "",
    // confirmNewPassword: "",
  });

  const [dataLoaded, setDataLoaded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [profileImagePreview, setProfileImagePreview] = useState("");

  const breadcrumbs = [
    { title: t("admin.faq.link_dashboard"), url: "/admin/dashboard" },
    { title: "User", url: "/admin/business/list/1" },
    { title: t("admin.faq.link_edit"), url: "" },
  ];

  useEffect(() => {
    userServices
      .Details(id)
      .then((res) => {
        const data = res?.data || {};
        setInitialValues({
          fullName: data.fullName || "",
          address: data.address || "",
          profile_image: "",
          country: data.country || "",
          city: data.city || "",
          state: data.state || "",
          zip_code: data.zip_code || "",
          // newPassword: data.newPassword || "",
          // confirmNewPassword: data.confirmNewPassword || "",
        });
        setProfileImagePreview(data.profile_image || "");
        setDataLoaded(true);
      })
      .catch((err) => {
        console.error("Error fetching user details", err);
        setDataLoaded(true);
      });
  }, [id]);

  const handleFileChange = (event, fieldName, setFieldValue, setPreview) => {
    const file = event.target.files[0];
    if (file) {
      setFieldValue(fieldName, file);
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      {dataLoaded && (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validateOnBlur={false}
          validate={(values) => {
            const error = {};
            if (!values.fullName?.trim()) error.fullName = "Full name is required";
            if (!values.address?.trim()) error.address = "Address is required";
            if (!values.country) error.country = "Country is required";
            if (!values.city?.trim()) error.city = "City is required";
            if (!values.state?.trim()) error.state = "State is required";
            if (values.zip_code && !/^[A-Za-z0-9][A-Za-z0-9 -]{1,9}$/.test(values.zip_code)) error.zip_code = "Invalid post code format";
            return error;
          }}
          onSubmit={(values, { setSubmitting }) => {
            console.log("Submitting form...");
            setSubmitted(true);
            globalLoader(true);

            const formData = new FormData();
            formData.append("o_id", id);
            formData.append("fullName", values.fullName.trim());
            formData.append("address", values.address.trim());
            formData.append("country", values.country);
            formData.append("city", values.city.trim());
            formData.append("state", values.state.trim());
            formData.append("zip_code", values.zip_code);

            // if (isResetPassword) {
            //   formData.append("newPassword", values.newPassword);
            //   formData.append("confirmNewPassword", values.confirmNewPassword);
            // }

            if (values.profile_image instanceof File) {
              formData.append("profile_image", values.profile_image);
            }

            userServices
              .Edit(formData)
              .then((response) => {
                setSubmitting(false);
                globalLoader(false);
                if (response.success) {
                  Swal.fire({
                    icon: "success",
                    text: response.message,
                    ...SWAL_SETTINGS,
                  });
                  setTimeout(() => navigate(`/admin/user/list/1`), 1000);
                } else {
                  Swal.fire({
                    icon: "error",
                    text: handleServerValidations(response),
                    ...SWAL_SETTINGS,
                  });
                  setSubmitted(false);
                }
              })
              .catch((error) => {
                console.error("Submit error: ", error);
                setSubmitted(false);
                globalLoader(false);
              });
          }}>
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <div className="row row-sm">
                <div className="col-lg-12 col-md-12 animation_fade">
                  <div className="card custom-card">
                    <div className="card-body">
                      <h6 className="main-content-label mb-3">Edit User</h6>
                      <div className="row row-sm">
                        {/* Full Name */}
                        <div className="col-lg-6 text-center form-group">
                          <label className="text-left d-flex">
                            Full Name:<span className="requirestar">*</span>
                          </label>
                          <input name="fullName" type="text" onChange={handleChange} onBlur={handleBlur} value={values.fullName} className="form-control" placeholder="Enter full name" />
                          <span className="text-danger d-flex text-left">{errors.fullName && touched.fullName && t(errors.fullName)}</span>
                        </div>

                        {/* Country */}
                        <div className="col-lg-6 text-center form-group">
                          <label className="text-left d-flex">
                            Country:<span className="requirestar">*</span>
                          </label>
                          <input name="country" type="text" onChange={handleChange} onBlur={handleBlur} value={values.country} className="form-control" placeholder="Enter country" />
                          <span className="text-danger d-flex text-left">{errors.country && touched.country && t(errors.country)}</span>
                        </div>

                        {/* City */}
                        <div className="col-lg-6 text-center form-group">
                          <label className="text-left d-flex">
                            City:<span className="requirestar">*</span>
                          </label>
                          <input name="city" type="text" onChange={handleChange} onBlur={handleBlur} value={values.city} className="form-control" placeholder="Enter city" />
                          <span className="text-danger d-flex text-left">{errors.city && touched.city && t(errors.city)}</span>
                        </div>

                        {/* State */}
                        <div className="col-lg-6 text-center form-group">
                          <label className="text-left d-flex">
                            State:<span className="requirestar">*</span>
                          </label>
                          <input name="state" type="text" onChange={handleChange} onBlur={handleBlur} value={values.state} className="form-control" placeholder="Enter state" />
                          <span className="text-danger d-flex text-left">{errors.state && touched.state && t(errors.state)}</span>
                        </div>

                        {/* Post Code */}
                        <div className="col-lg-6 text-center form-group">
                          <label className="text-left d-flex">Post Code:</label>
                          <input name="zip_code" type="text" onChange={handleChange} onBlur={handleBlur} value={values.zip_code} className="form-control" placeholder="Enter post code" />
                          <span className="text-danger d-flex text-left">{errors.zip_code && touched.zip_code && t(errors.zip_code)}</span>
                        </div>

                        {/* Address */}
                        <div className="col-lg-12 text-center form-group">
                          <label className="text-left d-flex">
                            Address:<span className="requirestar">*</span>
                          </label>
                          <textarea name="address" className="form-control" rows="3" value={values.address} onChange={handleChange} onBlur={handleBlur} placeholder="Enter complete address" />
                          <span className="text-danger d-flex text-left">{errors.address && touched.address && t(errors.address)}</span>
                        </div>

                        {/* <div className="col-lg-12 text-center form-group">
                          <div className="">
                            <label htmlFor="isResetPassword" className="ckbox cp">
                              <p className="text-start" style={{ paddingLeft: "1.5rem", paddingTop: "0.1rem" }}>
                                Check, if you want to reset password
                              </p>
                              <input id="isResetPassword" value={isResetPassword} type="checkbox" className="form-check select-check cp" onChange={() => setIsResetPassword(!isResetPassword)} />
                              <span></span>
                            </label>
                          </div>

                          {isResetPassword && (
                            <div style={{ marginTop: "-1.5rem" }}>
                              <div>
                                <label className="text-left d-flex" htmlFor="newPassword">
                                  New Password:<span className="requirestar">*</span>
                                </label>
                                <input id="newPassword" name="newPassword" type="password" onChange={handleChange} onBlur={handleBlur} value={values.newPassword} className="form-control" placeholder="Enter new password" />
                                <span className="text-danger d-flex text-left">{errors.newPassword && touched.newPassword && t(errors.newPassword)}</span>
                              </div>

                              <div style={{ marginTop: "1rem" }}>
                                <label className="text-left d-flex" htmlFor="confirmNewPassword">
                                  Confirm New Password:<span className="requirestar">*</span>
                                </label>
                                <input id="confirmNewPassword" name="confirmNewPassword" type="password" onChange={handleChange} onBlur={handleBlur} value={values.confirmNewPassword} className="form-control" placeholder="Enter confirm new password" />
                                <span className="text-danger d-flex text-left">{errors.confirmNewPassword && touched.confirmNewPassword && t(errors.confirmNewPassword)}</span>
                              </div>
                            </div>
                          )}
                        </div> */}

                        {/* Profile Image */}
                        <div className="col-lg-6 text-center form-group">
                          <label className="text-left d-flex">Profile Image:</label>
                          <input name="profile_image" type="file" accept="image/*" onChange={(e) => handleFileChange(e, "profile_image", setFieldValue, setProfileImagePreview)} className="form-control" />
                          {profileImagePreview && (
                            <div className="mt-2">
                              <img src={profileImagePreview} alt="Profile Preview" style={{ maxWidth: "150px", maxHeight: "150px", objectFit: "cover" }} className="img-thumbnail" />
                            </div>
                          )}
                        </div>

                        {/* Buttons */}
                        <div className="col-lg-12 mt-3">
                          <button className="btn btn-main-primary signbtn mr-2" type="submit" disabled={submitted}>
                            <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                            {t("Update")}
                          </button>
                          <button className="btn ripple btn-secondary" type="button" disabled={submitted} onClick={() => navigate(-1)}>
                            <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                            {t("Cancel")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      )}
    </>
  );
};

export default UserEdit;
